import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

interface Play {
  id: number;
  title: string;
  audience: string;
  image_url: string | null;
}

interface Event {
  id: number;
  title: string;
  audience: string;
  image_url: string | null;
}

const PlayPage = () => {
  const [plays, setPlays] = useState<Play[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Clean up invalid URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let hasInvalidParams = false;

    // Check for invalid month parameters
    const monthParam = urlParams.get('month');
    if (monthParam) {
      const parsedDate = new Date(monthParam + '-01');
      const currentYear = new Date().getFullYear();

      if (isNaN(parsedDate.getTime()) || 
          parsedDate.getFullYear() < 2020 || 
          parsedDate.getFullYear() > currentYear + 1) {
        urlParams.delete('month');
        hasInvalidParams = true;
      }
    }

    // Clean up URL if invalid parameters found
    if (hasInvalidParams) {
      const newUrl = new URL(window.location.href);
      newUrl.search = urlParams.toString();
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []);

  useEffect(() => {
    fetch(`/api/plays?lang=${i18n.language}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((data) =>
        setPlays(
          data.map((play: any) => ({
            id: play.id,
            title: play.title,
            audience: play.audience,
            image_url: play.image_url,
          }))
        )
      )
      .catch((err) => console.error("Plays fetch error:", err));

    fetch(`/api/events?lang=${i18n.language}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((data) =>
        setEvents(
          data.map((event: any) => ({
            id: event.id,
            title: event.title,
            audience: event.audience,
            image_url: event.image_url,
          }))
        )
      )
      .catch((err) => console.error("Events fetch error:", err));
  }, [i18n.language]);

  return (
          <section className="w-full flex flex-col items-center px-4 md:px-24 py-1 mt-8 bg-black text-white font-sans">
      <SEO
        title={t("pageTitles.plays")}
        description="Tiyatro Kumpanyası oyunları ve etkinlikleri. Berlin'de tiyatro gösterileri ve kültürel etkinlikler. Hemen bilet alın!"
        keywords="tiyatro oyunları, berlin tiyatro, theater berlin, kültürel etkinlikler, tiyatro gösterileri"
      />
      <h1 className="sr-only">{t("pageTitles.plays")}</h1>

      {/* Plays */}
      <h2 className="text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mb-10">
        {t("plays.plays")}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl w-full mb-16">
        {plays.map((play) => (
          <div
            key={play.id}
            className="text-center cursor-pointer max-w-[460px] mx-auto"
            onClick={() => navigate(`/plays/${play.id}`)}
          >
            {play.image_url && (
              <div className="w-full aspect-[2/3] rounded-xl overflow-hidden">
                <img
                  src={play.image_url}
                  alt={play.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <h3 className="mt-4 text-lg font-semibold hover:text-brand-red-text">
              {play.title}
            </h3>
            <p className="text-sm text-gray-400">{play.audience}</p>
          </div>
        ))}
      </div>

      {/* Events */}
      <h2 className="text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mb-10">
        {t("plays.events")}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl w-full">
        {events.map((event) => (
          <div
            key={event.id}
            className="text-center cursor-pointer max-w-[460px] mx-auto"
            onClick={() => navigate(`/events/${event.id}`)}
          >
            {event.image_url && (
              <div className="w-full aspect-[2/3] rounded-xl overflow-hidden">
                <img
                  src={event.image_url}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <h3 className="mt-4 text-lg font-semibold hover:text-brand-red-text">
              {event.title}
            </h3>
            <p className="text-sm text-gray-400">{event.audience}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PlayPage;